import { ActivityV2 } from 'models/activityV2'
import type { EmailMessage } from 'models/emailIntegration'
import { EventGroup } from './types'

export const groupEventsByDate = (events: ActivityV2[]): EventGroup[] => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const groups: { [key: string]: ActivityV2[] } = {}

  events.forEach((event) => {
    const eventDate = new Date(event.created_at)
    const dateKey = eventDate.toDateString()

    if (!groups[dateKey]) {
      groups[dateKey] = []
    }
    groups[dateKey].push(event)
  })

  return Object.entries(groups)
    .map(([dateKey, events]) => {
      const date = new Date(dateKey)
      const isToday = date.toDateString() === today.toDateString()
      const isYesterday = date.toDateString() === yesterday.toDateString()

      let relativeDate = 'Today'
      if (isYesterday) {
        relativeDate = 'Yesterday'
      } else if (!isToday) {
        // Get day of week
        relativeDate = date.toLocaleDateString('en-US', { weekday: 'long' })
      }

      const displayDate = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })

      return {
        date: dateKey,
        displayDate,
        relativeDate,
        events: events.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ),
        isToday,
        isYesterday,
      }
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

/**
 * Build a minimal EmailMessage from an activity event for the email preview modal.
 * Extracts subject, snippet, and outbound flag from field_history_objects.
 */
export const buildEmailFromEvent = (event: ActivityV2): EmailMessage => {
  const objectId = event.field_history_objects?.[0]?.object_id || event.id

  const emailData: EmailMessage = {
    id: objectId,
    outbound: false,
    related_contacts: [],
    sender: null,
    to: null,
    cc: null,
    subject: '',
    snippet: '',
    date: event.created_at,
  }

  event.field_history_objects?.forEach((field) => {
    if (field.field_name === 'subject') {
      emailData.subject = field.value || ''
    } else if (field.field_name === 'snippet') {
      emailData.snippet = field.value || ''
    } else if (field.field_name === 'outbound') {
      emailData.outbound = field.value || false
    }
  })

  return emailData
}

export const formatEventTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}
